/**
 * Rich "Tracked" speaker card — shown in the Speakers roster for tracked speakers.
 * Presentational only; receives a pre-derived SpeakerSummary.
 */

import { Badge } from "@d12labs/ui/badge";
import { cn } from "@d12labs/utils";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Star } from "lucide-react";
import { overallScoreColor } from "../types";
import { AffiliationMenu } from "./affiliation-menu";
import { Avatar } from "./avatar";
import { Delta } from "./delta";
import { ScoreBar, scoreToneClass } from "./score-bar";
import { Sparkline } from "./sparkline";

export type SpeakerSummary = {
	id: string;
	name: string;
	email: string | null;
	isMe: boolean;
	tracked: boolean;
	/** Latest overall score (0–100). */
	score: number;
	/** Last minus first overall score. */
	delta: number;
	analyses: number;
	/** Rounded mean of the 10 most-recent analyses by meeting date; null when none. */
	averageScore: number | null;
	/** Min overallScore across ALL analyses; null when none. Never 0. */
	minScore: number | null;
	/** Max overallScore across ALL analyses; null when none. Never 0. */
	maxScore: number | null;
	/** Persisted internal/external classification; null = unset. */
	affiliation: "internal" | "external" | null;
	/** Overall score history (oldest → newest). */
	sparkline: number[];
	topDimension: { label: string; score: number };
	weakDimension: { label: string; score: number };
	/** Relative date string, e.g. "Today", "3d ago". */
	lastSeen: string;
	onToggleTrack?: () => void;
	onSetAffiliation?: (affiliation: "internal" | "external" | null) => void;
};

export function SpeakerCard({ speaker }: { speaker: SpeakerSummary }) {
	const scoreParts = speaker.score.toFixed(2).split(".");
	const scoreInt = scoreParts[0] ?? "0";
	const scoreDec = scoreParts[1] ?? "00";
	// noAnalyses: fully sparse fallback (score "—", "—" tiles)
	const noAnalyses = speaker.analyses === 0;
	// noTrend: has score/dimensions but not enough data for the sparkline
	const noTrend = speaker.sparkline.length < 2;

	return (
		<div
			className={cn(
				"group flex flex-col rounded-lg border bg-surface-1 p-4 transition-colors",
				speaker.isMe ? "border-primary/40" : "border-hairline hover:border-hairline-strong",
			)}
		>
			{/* Header row: avatar + name/email + score + delta + star */}
			<div className="flex items-start gap-3">
				<Avatar
					name={speaker.name}
					size="lg"
					className={cn(
						speaker.isMe && "ring-2 ring-primary/60 ring-offset-2 ring-offset-surface-1",
					)}
				/>
				<div className="min-w-0 flex-1">
					<div className="flex items-center gap-1.5">
						<span className="truncate text-body-sm font-medium text-ink">{speaker.name}</span>
						{speaker.isMe && (
							<Badge tone="primary" dot="var(--color-primary)">
								You
							</Badge>
						)}
					</div>
					<div className="truncate text-meta text-ink-subtle">{speaker.email ?? "No email"}</div>
				</div>
				<div className="flex flex-col items-end gap-1 leading-none">
					<div className="flex items-center gap-1.5">
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								speaker.onToggleTrack?.();
							}}
							className="flex items-center"
						>
							<Star
								className={cn(
									"size-4 transition-colors",
									speaker.tracked
										? "fill-amber-400 text-amber-400"
										: "text-ink-tertiary hover:text-amber-400",
								)}
							/>
						</button>
						{noAnalyses ? (
							<span className="text-[22px] font-semibold tabular-nums leading-none text-ink-tertiary">
								—
							</span>
						) : (
							<span
								className={cn(
									"text-[22px] font-semibold tabular-nums leading-none",
									overallScoreColor(speaker.score),
								)}
							>
								{scoreInt}
								<span className="text-meta text-ink-tertiary">.{scoreDec}</span>
							</span>
						)}
					</div>
					<Delta value={speaker.delta} />
				</div>
			</div>

			{/* Sparkline — gated on ≥2 analyses */}
			<div className="mt-3">
				{noTrend ? (
					<p className="text-meta text-ink-tertiary">Needs 2+ analyses for trend</p>
				) : (
					<Sparkline
						values={speaker.sparkline}
						height={32}
						fill={false}
						color={speaker.delta < 0 ? "var(--color-warning)" : "var(--color-success)"}
					/>
				)}
			</div>

			{/* Strength + Focus tiles — shown whenever ≥1 analysis exists */}
			<div className="mt-3 grid grid-cols-2 gap-2">
				<div className="rounded-md border border-hairline bg-surface-2/60 px-2.5 py-1.5">
					<div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-tertiary">
						<span className="size-1.5 rounded-full bg-semantic-success flex-none" />
						Strength
					</div>
					<div className="mt-0.5 flex items-baseline justify-between gap-1">
						<span className="truncate text-body-sm text-ink">
							{noAnalyses ? "—" : speaker.topDimension.label}
						</span>
						{!noAnalyses && (
							<span
								className={cn(
									"shrink-0 font-mono text-meta tabular-nums",
									scoreToneClass(speaker.topDimension.score),
								)}
							>
								{speaker.topDimension.score.toFixed(1)}
							</span>
						)}
					</div>
					{!noAnalyses && <ScoreBar value={speaker.topDimension.score} height={3} />}
				</div>
				<div className="rounded-md border border-hairline bg-surface-2/60 px-2.5 py-1.5">
					<div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-tertiary">
						<span className="size-1.5 rounded-full bg-warning flex-none" />
						Focus
					</div>
					<div className="mt-0.5 flex items-baseline justify-between gap-1">
						<span className="truncate text-body-sm text-ink">
							{noAnalyses ? "—" : speaker.weakDimension.label}
						</span>
						{!noAnalyses && (
							<span
								className={cn(
									"shrink-0 font-mono text-meta tabular-nums",
									scoreToneClass(speaker.weakDimension.score),
								)}
							>
								{speaker.weakDimension.score.toFixed(1)}
							</span>
						)}
					</div>
					{!noAnalyses && <ScoreBar value={speaker.weakDimension.score} height={3} />}
				</div>
			</div>

			{/* Footer */}
			<div className="mt-3 flex items-center justify-between border-t border-hairline pt-2.5 text-meta text-ink-subtle">
				<span className="flex items-center gap-1">
					{speaker.analyses} {speaker.analyses === 1 ? "analysis" : "analyses"} · avg{" "}
					{speaker.averageScore ?? "—"} · min {speaker.minScore ?? "—"} / max{" "}
					{speaker.maxScore ?? "—"} · {speaker.lastSeen}
					{speaker.onSetAffiliation ? (
						<AffiliationMenu value={speaker.affiliation} onSet={speaker.onSetAffiliation} />
					) : null}
				</span>
				<Link
					to="/meeting-perf/speakers/$id"
					params={{ id: speaker.id }}
					className="flex items-center gap-1 text-ink-subtle transition-colors group-hover:text-ink"
				>
					View trend
					<ChevronRight className="size-3" />
				</Link>
			</div>
		</div>
	);
}
